// 상담 챗봇 API 클라이언트 — 관리자용 /api/v2/counsel/*
//
// 서버 라우트: server/src/v2/routes/counsel/index.ts
// 도메인 타입 SSOT: shared/counsel.ts
// 스펙: docs/spec/counsel-chat-spec.md
//
// ⚠️ 고객(비로그인)이 쓰는 /api/v2/talk/* 는 인증 헤더가 붙으면 안 되므로 이 파일이 아니라
//    src/api/talk-public.ts 에 따로 있다. 두 파일을 합치지 말 것 — authJson 이 섞여 들어가면
//    로그인 안 한 고객 화면에서 401 이 난다.

import { authForm, authJson } from "./_client";
import type {
  CounselChannel,
  CounselConversation,
  CounselCrmMatch,
  CounselDraft,
  CounselKnowledge,
  CounselKnowledgeInput,
  CounselKnowledgeSuggestion,
  CounselLearningCase,
  CounselLearningFilter,
  CounselMessage,
} from "../../shared/counsel";
import type {
  CounselFeedbackSummary,
  CounselFeedbackVerdict,
  CounselLearningRun,
} from "../../shared/counsel-feedback";

const API_V2 = `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/v2`;

function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  return authJson<T>(url, init, "truthy");
}

// ─── 채널 (공개 상담 링크) ──────────────────────────────────

export interface CounselChannelInfo {
  channel: CounselChannel;
  unansweredConversations: number;
  /** 롤링 배포 호환용 옛 필드명. 값은 unansweredConversations 와 같다. */
  pendingDrafts: number;
}

export async function fetchCounselChannel(): Promise<CounselChannelInfo> {
  return fetchJson<CounselChannelInfo>(`${API_V2}/counsel/channel`);
}

export async function updateCounselChannel(patch: {
  displayName?: string;
  greeting?: string | null;
  autoDraft?: boolean;
  requirePhone?: boolean;
  status?: CounselChannel["status"];
}): Promise<CounselChannel> {
  const { channel } = await fetchJson<{ channel: CounselChannel }>(
    `${API_V2}/counsel/channel`,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
  return channel;
}

export async function rotateCounselToken(): Promise<CounselChannel> {
  const { channel } = await fetchJson<{ channel: CounselChannel }>(
    `${API_V2}/counsel/channel/rotate-token`,
    { method: "POST" },
  );
  return channel;
}

// ─── 대화 ───────────────────────────────────────────────────

export interface CounselConversationsResult {
  conversations: CounselConversation[];
  unansweredConversations: number;
  /** 롤링 배포 호환용 옛 필드명. 값은 unansweredConversations 와 같다. */
  pendingDrafts: number;
}

export async function fetchCounselConversations(): Promise<CounselConversationsResult> {
  return fetchJson<CounselConversationsResult>(`${API_V2}/counsel/conversations`);
}

export interface CounselUnansweredCount {
  unansweredConversations: number;
  /** 롤링 배포 호환용 옛 필드명. 값은 unansweredConversations 와 같다. */
  pendingDrafts: number;
}

/** LNB 숫자 배지용 — 대화 목록을 싣지 않고 답장을 기다리는 상담 수만 읽는다. */
export async function fetchCounselUnansweredCount(): Promise<CounselUnansweredCount> {
  return fetchJson<CounselUnansweredCount>(`${API_V2}/counsel/pending-drafts/count`);
}

/**
 * 확정된 사실 한 칸. 값이 없으면 화면이 "미확인"으로 그린다.
 * 서버가 되묻는 순서(통신사 → 가입유형 → 희망기종 → 용량·색상) 그대로 내려준다.
 */
export interface CounselSlotStatus {
  key: string;
  value: string | null;
  /** 견적을 내려면 있어야 하는 칸인가. */
  required: boolean;
  /** 그 필수 칸인데 아직 비어 있는가(챗봇이 다음에 물을 것). */
  missing: boolean;
}

export interface CounselConversationDetail {
  conversation: CounselConversation;
  messages: CounselMessage[];
  /** 승인 대기 중인 AI 초안. 없으면 null. */
  draft: CounselDraft | null;
  /**
   * 이 상담 상대가 이미 우리 CRM 고객이면 그 요약(task 488). 처음 문의하는 사람이면 null.
   * 번호를 안 남긴 상담도 null 이다.
   */
  crm: CounselCrmMatch | null;
  /** 이 대화에서 확정된 사실 8칸 — 담당자가 상황을 한눈에 본다(§6.8). */
  slots: CounselSlotStatus[];
  /**
   * 답변마다 달린 평가 요약 — **고객은 어떻게 봤나 / 우리는 어떻게 봤나**(§6.19).
   * 둘이 갈리는 답변이 가장 값진 신호다("우리는 잘 썼다는데 손님은 아니라고 한다").
   */
  feedback: Record<number, CounselFeedbackSummary>;
  /** 내가 누른 것 — 버튼의 눌린 상태. */
  myFeedback: Record<number, CounselFeedbackVerdict>;
  /** 4초 변경 감지 값. 화면에 보이는 원장 중 하나라도 바뀌면 달라진다. */
  revision: string;
}

export interface CounselConversationRevision {
  revision: string;
}

/**
 * 답변 하나를 평가한다(내부자). `verdict: null` 이면 취소다.
 *
 * ⚠️ 이 평가는 챗봇을 즉시 바꾸지 않는다. 원장에 쌓였다가 개발자가 돌리는 배치가 읽어
 *    후보를 만들고, 그 후보도 꺼진 채로 저장돼 사람이 켜야 실전에 실린다.
 */
export async function rateCounselMessage(
  conversationId: number,
  messageId: number,
  verdict: CounselFeedbackVerdict | null,
  extra?: { reasons?: string[]; reasonText?: string | null },
): Promise<void> {
  await fetchJson(
    `${API_V2}/counsel/conversations/${conversationId}/messages/${messageId}/feedback`,
    { method: "POST", body: JSON.stringify({ verdict, ...(extra ?? {}) }) },
  );
}

/** 아직 학습에 안 실린 평가가 몇 건인가 — "가르칠 게 쌓였다"를 알리는 값. */
export async function fetchCounselPendingFeedback(): Promise<{
  total: number;
  up: number;
  down: number;
}> {
  return fetchJson<{ total: number; up: number; down: number }>(
    `${API_V2}/counsel/feedback/pending`,
  );
}

/** 지난 배치 회차 목록 — 언제 무엇을 반영했나(되돌리기의 근거). */
export async function fetchCounselLearningRuns(): Promise<{
  runs: CounselLearningRun[];
}> {
  return fetchJson<{ runs: CounselLearningRun[] }>(`${API_V2}/counsel/learning/runs`);
}

// ─── 이 초안에 무엇이 실렸나 (🔎 왜 이렇게 답했나) ──────────
//
// 훈련장 코치 패널과 같은 것을 인박스에서도 본다(읽기 전용). 서버가 이미 사람이 읽는 모양으로
// 풀어서 내려주므로 이 화면은 지식·예시·데이터를 따로 다시 조회하지 않는다.

export interface CounselDraftContextKnowledge {
  id: number;
  kind: string;
  kindLabel: string;
  category: string;
  categoryLabel: string;
  situation: string;
  /** 지금은 꺼져 있는 카드인가(실릴 당시에는 켜져 있었다). */
  enabled: boolean;
  /** 실린 뒤에 지워진 카드인가 — 답이 달라진 이유일 수 있어 감추지 않는다. */
  deleted: boolean;
}

export interface CounselDraftContextExample {
  id: number;
  category: string;
  categoryLabel: string;
  customerText: string;
  goodText: string;
}

export interface CounselDraftContextDatasource {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
}

export interface CounselDraftContext {
  draftId: number;
  conversationId: number;
  /**
   * log = 정식 주입 기록이 있다 · legacy = 지식 id 만 남은 옛 초안 · none = 기록 없음.
   * 옛 초안은 조용히 "기록 없음"으로 보여준다(에러가 아니다).
   */
  source: "log" | "legacy" | "none";
  recordedAt: string | null;
  knowledge: CounselDraftContextKnowledge[];
  exampleCount: number;
  examples: CounselDraftContextExample[];
  datasources: CounselDraftContextDatasource[];
  slots: CounselSlotStatus[];
  tokenEstimate: number;
}

/** 이 초안에 무엇이 실렸나 — 초안 확인 창·인박스에서 펼쳐 본다. */
export async function fetchCounselDraftContext(
  draftId: number,
): Promise<CounselDraftContext> {
  const { context } = await fetchJson<{ context: CounselDraftContext }>(
    `${API_V2}/counsel/drafts/${draftId}/context`,
  );
  return context;
}

export async function fetchCounselConversation(
  id: number,
): Promise<CounselConversationDetail> {
  return fetchJson<CounselConversationDetail>(`${API_V2}/counsel/conversations/${id}`);
}

/** 열어 둔 상담이 바뀌었는지만 확인한다 — 메시지·초안·CRM 본문은 싣지 않는다. */
export async function fetchCounselConversationRevision(
  id: number,
): Promise<CounselConversationRevision> {
  return fetchJson<CounselConversationRevision>(
    `${API_V2}/counsel/conversations/${id}/revision`,
  );
}

/** 답변 전송. draftId 를 주면 그 초안이 "보냄"으로 정리되고 실제 나간 문장이 기록된다. */
export async function sendCounselMessage(
  id: number,
  text: string,
  draftId?: number | null,
  images: File[] = [],
): Promise<CounselMessage> {
  if (images.length > 0) {
    const form = new FormData();
    form.append("text", text);
    if (draftId != null) form.append("draftId", String(draftId));
    for (const image of images) form.append("images", image);
    const { message } = await authForm<{ message: CounselMessage }>(
      `${API_V2}/counsel/conversations/${id}/send`,
      form,
    );
    return message;
  }
  const { message } = await fetchJson<{ message: CounselMessage }>(
    `${API_V2}/counsel/conversations/${id}/send`,
    { method: "POST", body: JSON.stringify({ text, draftId: draftId ?? undefined }) },
  );
  return message;
}

export async function discardCounselDraft(id: number, draftId: number): Promise<void> {
  await fetchJson(`${API_V2}/counsel/conversations/${id}/drafts/${draftId}/discard`, {
    method: "POST",
  });
}

/** 초안 다시 만들기 — 지식을 고친 뒤 결과를 보고 싶을 때. */
export async function redraftCounselAnswer(id: number): Promise<CounselDraft | null> {
  const { draft } = await fetchJson<{ draft: CounselDraft | null }>(
    `${API_V2}/counsel/conversations/${id}/redraft`,
    { method: "POST" },
  );
  return draft;
}

// ─── 테스트 상담 (링크 없이 챗봇 시험하기) ──────────────────
//
// 손님 링크를 열어 손님인 척 하지 않고, 인박스 안에서 방을 만들어 바로 물어본다.
// 서버는 실전과 **같은 초안 생성기**로 답을 만든 뒤 그 자리에서 발송 처리해 돌려준다.
// 계약: server/src/v2/routes/counsel/index.ts §테스트 상담

export interface CounselTestReply {
  /** 내가 손님 역할로 친 말. */
  customerMessage: CounselMessage;
  /** 챗봇이 답한 말(초안 생성 실패 시 안내 문장). */
  botMessage: CounselMessage;
  /** 이 답을 만든 초안. 🔎 "왜 이렇게 답했나"를 펼치는 데 쓴다. 실패했으면 null. */
  draft: CounselDraft | null;
}

export async function createCounselTestConversation(
  name?: string,
): Promise<CounselConversation> {
  const { conversation } = await fetchJson<{ conversation: CounselConversation }>(
    `${API_V2}/counsel/test-conversations`,
    { method: "POST", body: JSON.stringify(name ? { name } : {}) },
  );
  return conversation;
}

export async function sendCounselTestMessage(
  id: number,
  text: string,
): Promise<CounselTestReply> {
  return fetchJson<CounselTestReply>(
    `${API_V2}/counsel/test-conversations/${id}/messages`,
    { method: "POST", body: JSON.stringify({ text }) },
  );
}

export async function deleteCounselTestConversation(id: number): Promise<void> {
  await fetchJson(`${API_V2}/counsel/test-conversations/${id}`, { method: "DELETE" });
}

/**
 * 사장님이 한 말이 무엇이었나.
 *
 * 화면은 이 값으로 말풍선 표시를 고른다 — 질문에 «저장되지 않았습니다»를 붙이면 사장님은
 * 뭔가 실패한 줄 알고, 모호한 지시를 그냥 넘기면 안 고쳐진 채로 지나간다.
 */
export type CounselInstructIntent = "question" | "rule" | "unclear";

/** 훈련 창에서 오간 말 한 줄 — 다음 턴에 맥락으로 같이 보낸다. */
export interface CounselTrainerTurn {
  from: "owner" | "bot";
  text: string;
}

/** 챗봇에게 말을 건 결과 — 무엇으로 알아들었는지와, 실제로 규칙으로 저장됐는지. */
export interface CounselInstructResult {
  intent: CounselInstructIntent;
  reply: string;
  knowledge: CounselKnowledge | null;
  /** false 면 규칙으로 저장되지 않았다(질문이거나·그때만 맞는 지시·모호·장애). */
  saved: boolean;
}

/**
 * 훈련 창에서 챗봇에게 말을 건다 — **묻는 것과 시키는 것 둘 다** 여기로 간다.
 *
 * 대화 id 는 참고용이다("방금 그 답변" 같은 말을 판단하는 데 쓴다).
 * 정책이 바뀌었다는 지시처럼 대화와 무관한 것도 여기로 보낸다.
 *
 * ⚠️ `history` 를 **화면이 들고 있다가 매번 같이 보낸다.** 서버에 훈련 대화를 저장하지
 * 않는 것은 일부러다 — 이 창은 «기록»이 아니고, 진짜 기록은 쌓인 규칙 목록이다.
 * 대신 이력을 안 보내면 "그럼 그건?" 같은 이어지는 말을 못 알아듣는다.
 */
export async function instructCounselBot(
  id: number,
  text: string,
  history: CounselTrainerTurn[] = [],
): Promise<CounselInstructResult> {
  return fetchJson<CounselInstructResult>(
    `${API_V2}/counsel/conversations/${id}/instruct`,
    { method: "POST", body: JSON.stringify({ text, history }) },
  );
}

/** 지시로 쌓인 규칙 목록 — "내가 뭐라고 했더라"를 되짚는 자리. */
export async function listCounselInstructions(): Promise<CounselKnowledge[]> {
  const { items } = await fetchJson<{ items: CounselKnowledge[] }>(
    `${API_V2}/counsel/instructions`,
  );
  return items;
}

export async function closeCounselConversation(id: number): Promise<void> {
  await fetchJson(`${API_V2}/counsel/conversations/${id}/close`, { method: "POST" });
}

export async function reopenCounselConversation(id: number): Promise<void> {
  await fetchJson(`${API_V2}/counsel/conversations/${id}/reopen`, { method: "POST" });
}

// ─── 지식 (챗봇 학습) ───────────────────────────────────────

export async function fetchCounselKnowledge(): Promise<CounselKnowledge[]> {
  const { knowledge } = await fetchJson<{ knowledge: CounselKnowledge[] }>(
    `${API_V2}/counsel/knowledge`,
  );
  return knowledge;
}

export async function createCounselKnowledge(
  input: CounselKnowledgeInput,
): Promise<CounselKnowledge> {
  const { knowledge } = await fetchJson<{ knowledge: CounselKnowledge }>(
    `${API_V2}/counsel/knowledge`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return knowledge;
}

export async function updateCounselKnowledge(
  id: number,
  patch: Partial<CounselKnowledgeInput>,
): Promise<CounselKnowledge> {
  const { knowledge } = await fetchJson<{ knowledge: CounselKnowledge }>(
    `${API_V2}/counsel/knowledge/${id}`,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
  return knowledge;
}

export async function deleteCounselKnowledge(id: number): Promise<void> {
  await fetchJson(`${API_V2}/counsel/knowledge/${id}`, { method: "DELETE" });
}

// ─── 학습 (사람이 고른 사례 → 지식 규칙) ────────────────────
//
// 오간 대화를 전부 학습에 밀어 넣지 않는다 — 담당자가 "이건 잘 됐다"고 고른 사례만 규칙이 된다.
// 화면 흐름: 목록에서 체크 → [고른 사례로 규칙 만들기](AI 제안) → 다듬어 저장.

/** 학습 후보 몇 건이 쌓였나 — 상담 인박스 상단 안내용. */
export interface CounselLearningCounts {
  /** 사람이 고쳐 보냈고 아직 규칙으로 안 올라간 사례 수. */
  edited: number;
  /** 학습에 쓰겠다고 표시해 둔 사례 수. */
  picked: number;
}

export interface CounselLearningCasesResult {
  cases: CounselLearningCase[];
  counts: CounselLearningCounts;
}

export async function fetchCounselLearningCases(
  filter: CounselLearningFilter,
): Promise<CounselLearningCasesResult> {
  return fetchJson<CounselLearningCasesResult>(
    `${API_V2}/counsel/learning/cases?filter=${filter}`,
  );
}

export async function fetchCounselLearningCounts(): Promise<CounselLearningCounts> {
  const { counts } = await fetchJson<{ counts: CounselLearningCounts }>(
    `${API_V2}/counsel/learning/counts`,
  );
  return counts;
}

/** "이건 학습에 쓰자" 표시 토글. */
export async function pickCounselLearningCase(
  draftId: number,
  picked: boolean,
): Promise<void> {
  await fetchJson(`${API_V2}/counsel/learning/cases/${draftId}/pick`, {
    method: "POST",
    body: JSON.stringify({ picked }),
  });
}

/** 고른 사례들로 규칙 초안을 제안받는다(저장은 아직 안 한다). */
export async function suggestCounselLearningRule(
  draftIds: number[],
): Promise<CounselKnowledgeSuggestion> {
  const { suggestion } = await fetchJson<{ suggestion: CounselKnowledgeSuggestion }>(
    `${API_V2}/counsel/learning/suggest`,
    { method: "POST", body: JSON.stringify({ draftIds }) },
  );
  return suggestion;
}

/** 다듬은 규칙을 저장하고 근거 사례들을 "반영됨"으로 표시한다. */
export async function promoteCounselLearningRule(
  draftIds: number[],
  input: CounselKnowledgeInput,
): Promise<CounselKnowledge> {
  const { knowledge } = await fetchJson<{ knowledge: CounselKnowledge }>(
    `${API_V2}/counsel/learning/promote`,
    { method: "POST", body: JSON.stringify({ draftIds, ...input }) },
  );
  return knowledge;
}
